import { useCallback, useRef } from 'react';
import { Box } from '@mui/material';
import { FORM_ACTION, useLocalization, useSnackbar } from '@ui-framework/index';
import Form from '@product-framework/components/AdvancedForms/Form';
import {
  IconLibrary,
  useAvailabilitySlotTypes,
  PermissionButton,
  PageSection,
} from '@product-framework/index';
import { useNavigate } from 'react-router-dom';
import editSchema from './schema/edit.json';
import ScheduleComponent from './components/ScheduleComponent';
import validationSchema from './validations';
import DefaultSchedule from '../../utils/DefaultSchedule';
import EffectiveStartDate from './components/EffectiveStartDate';
import EffectiveEndDate from './components/EffectiveEndDate';

const FORM_ID = 'FORM_ID_AVAILABILITY_SP_DEFAULT_SCHEDULE';

function DefaultScheduleForm({
  params,
  selectedScheduleId,
  locationId = null,
  mode,
}) {
  const navigate = useNavigate();
  const { formatMessage } = useLocalization();
  const snackbar = useSnackbar();
  const formRef = useRef();
  const { slotTypes } = useAvailabilitySlotTypes();
  const serviceProviderId = params?.pageId;

  const locationUrl = locationId ? `/locations/${locationId}` : '';

  const action = mode;

  const onSubmit = async data => {
    const preparedData = DefaultSchedule.getUpsertObject(data, slotTypes);
    const responseDefaultSchedule = await DefaultSchedule.upsert({
      params,
      data: preparedData,
      defaultScheduleId: selectedScheduleId,
      locationId,
    });
    if (responseDefaultSchedule?.ok) {
      snackbar.showMessage(
        formatMessage({
          id: 'DEFAULT_SCHEDULE_WAS_SAVED_SUCCESSFULLY',
          defaultMessage: 'Default Schedule was saved successfully',
        }),
      );
      const nextData = DefaultSchedule.parseSuccessResponseForNextValue({
        schedule: responseDefaultSchedule?.data,
        slotTypes,
        serviceProviderId,
        formatMessage,
      });
      return {
        nextValues: nextData,
        errors: null,
        callback: () => {
          navigate(
            `/serviceproviders/edit/${serviceProviderId}${locationUrl}/availability/default-schedules`,
          );
        },
      };
    }

    let errors = [];
    if (responseDefaultSchedule?.errors) {
      errors = [...responseDefaultSchedule.errors];
    }

    return {
      nextValues: data,
      errors,
    };
  };

  const fetchDefaultValues = useCallback(async () => {
    if (action === FORM_ACTION.CREATE) {
      const defaultValues = {
        data: {
          effectiveStartDate: null,
          effectiveEndDate: null,
          schedule: DefaultSchedule.getDefaultValues(slotTypes, formatMessage),
          serviceProviderId,
        },
        errors: null,
      };
      return defaultValues;
    }
    // If Form is in Edit mode
    const response = await DefaultSchedule.fetch({
      params,
      defaultScheduleId: selectedScheduleId,
      locationId,
    });

    if (response?.ok) {
      const processedData = DefaultSchedule.parseSuccessResponseForNextValue({
        schedule: response?.data,
        slotTypes,
        serviceProviderId: params?.pageId,
        formatMessage,
      });
      const defaultValues = {
        data: processedData,
        errors: null,
      };
      // setCurrentValues(processedData);
      return defaultValues;
    }
    return {
      data: response.data,
      errors: response.errors,
    };
  }, [serviceProviderId, selectedScheduleId]);

  return (
    <PageSection
      id="ScrollTopElementDefaultScheduleForm"
      subSection
      label={
        FORM_ACTION.CREATE === action
          ? formatMessage({
              id: 'CREATE_SCHEDULE',
              defaultMessage: 'Create Schedule',
            })
          : formatMessage({
              id: 'EDIT_SCHEDULE',
              defaultMessage: 'Edit Schedule',
            })
      }>
      <Form
        submitWithReadOnlyFields
        formId={FORM_ID}
        ref={formRef}
        onSubmit={onSubmit}
        schema={editSchema}
        validationSchema={validationSchema(formatMessage)}
        permittedByDefault
        customComponents={{
          Schedule: ScheduleComponent,
          EffectiveStartDate,
          EffectiveEndDate,
        }}
        fetchDefaultValues={fetchDefaultValues}>
        <Box display="flex" justifyContent="center" gap={2} mt={3} mb={1}>
          <PermissionButton
            variant="contained"
            startIcon={<IconLibrary.Save />}
            color="primary"
            permissionName={
              FORM_ACTION.CREATE === action
                ? 'CAN_CREATE_SP_AVAILABILITY_DEFAULT_SCHEDULE'
                : 'CAN_UPDATE_SP_AVAILABILITY_DEFAULT_SCHEDULE'
            }
            onClick={() => formRef.current?.submit({ formId: FORM_ID })}>
            {formatMessage({
              id: 'SAVE_DEFAULT_SCHEDULE',
              defaultMessage: 'Save Default Schedule',
            })}
          </PermissionButton>
          <PermissionButton
            variant="contained"
            startIcon={<IconLibrary.Cancel />}
            color="primary"
            permissionName={
              FORM_ACTION.CREATE === action
                ? 'CAN_CREATE_SP_AVAILABILITY_DEFAULT_SCHEDULE'
                : 'CAN_UPDATE_SP_AVAILABILITY_DEFAULT_SCHEDULE'
            }
            onClick={() =>
              navigate(
                `/serviceproviders/edit/${serviceProviderId}${locationUrl}/availability/default-schedules`,
              )
            }>
            {formatMessage({
              id: 'CLOSE',
              defaultMessage: 'Close',
            })}
          </PermissionButton>
          <PermissionButton
            variant="contained"
            color="primary"
            permissionName={
              FORM_ACTION.CREATE === action
                ? 'CAN_CREATE_SP_AVAILABILITY_DEFAULT_SCHEDULE'
                : 'CAN_UPDATE_SP_AVAILABILITY_DEFAULT_SCHEDULE'
            }
            startIcon={<IconLibrary.Reset />}
            onClick={() => formRef.current?.reset()}>
            {formatMessage({
              id: 'RESET',
              defaultMessage: 'Reset',
            })}
          </PermissionButton>
        </Box>
      </Form>
    </PageSection>
  );
}

export default DefaultScheduleForm;
